// The VERA Brain on Home: the company's outcomes (construction: cost, schedule, quality) with numbers the server counts
// from its own records, and the intelligence layers everything VERA learns sits on. Shown only where the company's
// industry has a brain. Its words come from the server in the industry's language, or in English and are shown in
// the person's language through the same dictionary as the screens (EduTask's brain in Albanian). An owner of an empty workspace can start from the pack's demo firm.
import { useCallback, useEffect, useState } from "react";
import { crm, type BrainNow } from "../lib/crm";
import { T } from "../lib/i18n";
import { Orb, useStudio } from "./ui";

export function BrainCard({ owner, demo, stamp, onChanged }: { owner: boolean; demo: boolean; stamp?: string; onChanged: () => void }) {
  const { getToken, toast, go } = useStudio();
  const [brain, setBrain] = useState<BrainNow | null | undefined>(undefined);
  const [demoState, setDemoState] = useState<{ present: number; missing: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    crm.brain(getToken).then((r) => { setBrain(r.brain); setDemoState(r.demo ?? null); }).catch(() => setBrain(null));
  }, [getToken]);
  useEffect(load, [load, stamp]);
  if (!brain) return null;
  const empty = brain.layers.every((l) => l.records === 0);
  const loadDemo = async () => {
    setLoading(true);
    try {
      const run = await crm.importDemo(getToken);
      toast(run.summary);
      onChanged();
      load();
    } catch (e) {
      toast(e instanceof Error ? e.message : T("That didn't work. Please try again."));
    } finally {
      setLoading(false);
    }
  };
  return (
    <section className="card brain">
      <div className="row between">
        <div className="row" style={{ gap: 8 }}><Orb size={16} /><h2>{T("VERA Brain")}</h2></div>
        <span className="faint small">{T("Counted from your records, live")}</span>
      </div>
      {owner && demo && (empty || (demoState && demoState.present > 0 && demoState.missing > 0)) && (
        <div className="notice calm row between" style={{ marginTop: 10 }}>
          <span className="mute">{empty ? T("Your workspace is empty. Start from a demo firm with realistic projects, then make it yours.") : T("The demo firm has new records you don't have yet.")}</span>
          <button className="btn" disabled={loading} onClick={() => void loadDemo()}>{loading ? T("Loading…") : empty ? T("Load the demo firm") : T("Add the new demo records")}</button>
        </div>
      )}
      <div className="grid g3 brain-outcomes">
        {brain.outcomes.map((o) => (
          <div className="brain-outcome" key={o.id}>
            <b>{T(o.name)}</b>
            <div className="faint small">{T(o.about)}</div>
            <div className="list" style={{ marginTop: 8 }}>
              {o.signals.map((s) => (
                <div className="li brain-signal" key={s.label}>
                  <span className="grow">{T(s.label)}</span>
                  <b className={`num ${s.alert ? "alert" : ""}`}>{s.shown}</b>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="brain-layers" aria-label={T("Intelligence layers")}>
        {brain.layers.map((l) => (
          <button key={l.id} className="brain-layer" title={T(l.about)} onClick={() => go("workspace", { ask: T("What do you see on the layer:") + ` ${T(l.name)}`, fresh: true, title: T(l.name) })}>
            <span className="faint">{String(l.n).padStart(2, "0")}</span> {T(l.name)}{l.records ? <span className="faint"> · {l.records}</span> : null}
          </button>
        ))}
      </div>
    </section>
  );
}
